/* [ START OF MODULE - VER 2.10 (src/context/AuthContext.jsx) ] */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { AuthContext } from './useAuth';

// RMI-2026-480: root cause of a whole saga of "randomly signed out" reports
// traced to a user's system clock running ~2 hours fast -- gotrue-js logs a
// console warning when this happens ("Session as retrieved from URL expires
// in -3600s, should have been closer to 3600s") but nothing surfaces it to
// the user, who just experiences a silent, unexplained refresh-loop ->
// 429 rate-limit -> SIGNED_OUT cascade with zero indication of why. A
// freshly issued session should have close to its full lifetime left; if it
// already looks expired (or absurdly long-lived) the moment it's set, the
// client's clock, not the session, is the problem. Flags via sessionStorage
// (survives the OAuth redirect reload, unlike component state) so TigerGate
// can show a clear one-time message instead of leaving the user to guess.
const CLOCK_SKEW_STORAGE_KEY = 'tecc_clock_skew_drift_sec';
const CLOCK_SKEW_THRESHOLD_SEC = 300; // 5 min -- well beyond normal NTP/network drift
// RMI-2026-542: driftSec was `now - expires_at` with no offset for the
// token's own lifetime -- for ANY healthy, freshly-issued session that's
// approximately `-expires_in` (about -3600s for Supabase's default 1h
// tokens), which always exceeds the 300s threshold regardless of the
// client's actual clock. The original RMI-2026-480 report (a clock running
// ~2h fast) happened to produce an even larger number that still read as
// "obviously wrong," so the bug went unnoticed, but this fired as a false
// positive for every normal sign-in too. Fixed to compare the session's
// ACTUAL remaining lifetime against what a freshly issued token's remaining
// lifetime should be (expires_in) -- that difference is what actually
// reflects client/server clock disagreement.
const checkClockSkew = (session) => {
  if (!session?.expires_at || !session?.expires_in) return;
  const remainingSec = session.expires_at - Math.round(Date.now() / 1000);
  const driftSec = remainingSec - session.expires_in;
  if (Math.abs(driftSec) > CLOCK_SKEW_THRESHOLD_SEC) {
    console.warn(`[AuthContext] Client clock skew detected (${driftSec}s) -- a freshly issued session's remaining lifetime (${remainingSec}s) doesn't match its expires_in (${session.expires_in}s).`);
    try { sessionStorage.setItem(CLOCK_SKEW_STORAGE_KEY, String(driftSec)); } catch (_) { /* ignore */ }
  }
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [deactivated, setDeactivated] = useState(false); // RMI-2026-313: hard login block
  const profileRef       = useRef(null);
  const inflightFetchRef = useRef(null);
  const failsafeRef      = useRef(null); // ref so initializeAuth can cancel it on success

  useEffect(() => {
    let isMounted = true;

    const fetchProfile = async (userId) => {
      if (inflightFetchRef.current) {
        return inflightFetchRef.current;
      }

      const fetchPromise = (async () => {
        try {
          console.log("[AuthContext] Fetching Matrix Profile for:", userId);

          const { data, error } = await supabase
            .from('profiles_v2')
            .select('id, profile_type, tier_level, client_id, client_access_level, first_name, last_name, employment_type, email, calendar_token, timezone, landing_view, account_id, org_role, rfp_access, mobile_home, is_active')
            .eq('id', userId)
            .single();

          if (error && error.code !== 'PGRST116') throw error;

          // RMI-2026-313: hard login block. A deactivated account (is_active=false)
          // is signed out immediately and shown the deactivated notice. The Auth
          // ban (set server-side by api/account-active.js) additionally blocks new
          // sign-in and token refresh; this gate covers the live-token window.
          if (data && data.is_active === false) {
            if (isMounted) { setDeactivated(true); setProfile(null); profileRef.current = null; }
            supabase.auth.signOut().catch(() => {});
            return;
          }

          // Fire-and-forget calendar token generation — not on the critical path
          if (data && !data.calendar_token) {
            const newToken = crypto.randomUUID().replace(/-/g, '');
            data.calendar_token = newToken;
            supabase
              .from('profiles_v2')
              .update({ calendar_token: newToken })
              .eq('id', userId)
              .then(({ error: tokenErr }) => {
                if (tokenErr) console.warn('[AuthContext] Calendar token persist failed:', tokenErr);
              });
          }

          console.log("[AuthContext] Profile Payload:", data || 'NULL');
          if (isMounted) {
            setProfile(data || null);
            profileRef.current = data || null;
          }
        } catch (err) {
          if (err?.name === 'AbortError' || err?.message?.includes('AbortError') || err?.message?.includes('Lock broken')) {
            console.log('[AuthContext] Profile fetch aborted (lock handoff) — onAuthStateChange will recover.');
          } else {
            console.error("[AuthContext] Profile Fetch Error:", err);
          }
          if (isMounted) setProfile(null);
        } finally {
          inflightFetchRef.current = null;
        }
      })();

      inflightFetchRef.current = fetchPromise;
      return fetchPromise;
    };

    const initializeAuth = async () => {
      try {
        console.log("[AuthContext] Initializing boot sequence...");

        const hash = window.location.hash;
        const isOAuthCallback = hash.includes('access_token') || hash.includes('type=signup') || hash.includes('type=magiclink');
        if (isOAuthCallback) {
          return;
        }

        const { data: { session: activeSession }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (isMounted) {
          setSession(activeSession);
          // RMI-2026-542: getSession() restores whatever's cached in storage,
          // not a freshly issued token -- a healthy session partway through
          // its lifetime has less than its full expires_in remaining by
          // design, so checking clock skew here produced false positives on
          // ordinary page loads. Only check on events that guarantee a fresh
          // issuance (SIGNED_IN / TOKEN_REFRESHED, handled below).
          if (activeSession?.user) {
            await fetchProfile(activeSession.user.id);
          } else {
            setProfile(null);
          }
          // Cancel the failsafe — we completed successfully, it must not fire
          clearTimeout(failsafeRef.current);
          setIsAuthLoading(false);
        }
      } catch (err) {
        if (err?.name === 'AbortError' || err?.message?.includes('AbortError') || err?.message?.includes('Lock broken')) {
          console.log('[AuthContext] Session init aborted (lock handoff) — onAuthStateChange will recover.');
          // Don't drop isAuthLoading — let onAuthStateChange handle it
        } else {
          console.error("[AuthContext] Session Error:", err);
          clearTimeout(failsafeRef.current);
          if (isMounted) setIsAuthLoading(false);
        }
      }
    };

    initializeAuth();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.log(`[AuthContext] Auth Event Triggered: ${event}`);
      if (!isMounted) return;

      if (event === 'TOKEN_REFRESHED') {
        setSession(newSession);
        // RMI-2026-542: a refresh always issues a fresh token (remaining
        // lifetime should equal the full expires_in), making this one of the
        // most reliable, frequent places to actually catch clock skew --
        // previously skipped entirely.
        checkClockSkew(newSession);
        return;
      }

      setSession(newSession);
      // RMI-2026-542: only SIGNED_IN guarantees a freshly issued session
      // (remaining lifetime == expires_in by construction). INITIAL_SESSION
      // and other events can carry a session that's legitimately partway
      // through its lifetime, which isn't a clock-skew signal.
      if (event === 'SIGNED_IN') checkClockSkew(newSession);

      if (newSession?.user) {
        if (!profileRef.current || profileRef.current.id !== newSession.user.id) {
          setIsAuthLoading(true);
          await fetchProfile(newSession.user.id);
          // Cancel failsafe here too in case this path beat initializeAuth
          clearTimeout(failsafeRef.current);
          setIsAuthLoading(false);
        }
      } else if (event === 'SIGNED_OUT') {
        setProfile(null);
        clearTimeout(failsafeRef.current);
        setIsAuthLoading(false);
      }
    });

    // Failsafe — last resort only. Stored in ref so initializeAuth can
    // cancel it the moment auth completes successfully.
    failsafeRef.current = setTimeout(() => {
      console.warn("[AuthContext] Failsafe Triggered. Force dropping lock.");
      if (isMounted) setIsAuthLoading(false);
    }, 2500);

    // RMI-2026-479: this listener is live from the moment AuthProvider mounts
    // -- including the very page load an OAuth redirect (Google) lands back
    // on. The tab going hidden-then-visible during that redirect can fire a
    // visibilitychange event on that fresh load, racing with the SDK's own
    // in-flight hash parsing (detectSessionInUrl) that's establishing the
    // real session. supabaseClient.js's no-op `lock` (a deliberate fix for
    // an earlier, unrelated navigation bug) removed the serialization that
    // used to keep concurrent auth calls like these from interleaving, so
    // this handler's own getSession() could resolve in the narrow window
    // before the session is persisted, see nothing, fall through to
    // refreshSession() (which also fails -- no refresh token yet either),
    // and used to explicitly clear session/profile -- wiping out a session
    // onAuthStateChange had *just* legitimately set. That produced exactly
    // the "dashboard renders for a moment, then bounces back to login"
    // symptom reported live. getSession() already auto-refreshes internally
    // when autoRefreshToken is on (it is here), so the manual refreshSession
    // fallback was mostly redundant; a genuinely dead session is already
    // handled correctly, race-free, by onAuthStateChange's own SIGNED_OUT
    // branch above. This handler now only ever acts on a session it can
    // positively confirm -- it never clears state on a negative result.
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!isMounted) return;

      const { data: { session: activeSession } } = await supabase.auth.getSession();
      if (!isMounted) return;

      if (activeSession) {
        setSession(activeSession);
        if (!profileRef.current) await fetchProfile(activeSession.user.id);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      isMounted = false;
      clearTimeout(failsafeRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn('[AuthContext] signOut error:', e);
    }
    Object.keys(localStorage)
      .filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'))
      .forEach(k => localStorage.removeItem(k));
    localStorage.removeItem('tecc-auth-session');
    // Hard reload after explicit logout so Supabase cannot restore the session
    // from its internal storage on the next page load. replace() keeps the URL
    // the same but removes the pre-logout page from browser history.
    window.location.replace(window.location.pathname);
  }, []);

  const contextValue = useMemo(() => ({
    session,
    profile,
    isAuthLoading,
    logout
  }), [session, profile, isAuthLoading, logout]);

  if (deactivated) {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0b1b2b', color: '#fff', fontFamily: 'Poppins, system-ui, sans-serif', padding: '24px', textAlign: 'center' }}>
        <div style={{ maxWidth: '420px' }}>
          <div style={{ fontSize: '2.4rem', marginBottom: '12px' }}>&#128274;</div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 800, margin: '0 0 10px' }}>Account deactivated</h1>
          <p style={{ fontSize: '0.9rem', lineHeight: 1.6, color: '#c3d0dd', margin: '0 0 22px' }}>
            Your access to Tiger&rsquo;s Eye has been turned off. Please contact an administrator if you believe this is a mistake.
          </p>
          <button
            onClick={() => { try { localStorage.clear(); } catch (err) { console.warn('[Auth] Error clearing storage:', err.message); } window.location.replace(window.location.pathname); }}
            style={{ padding: '10px 22px', background: '#5899CD', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/* [ END OF MODULE - VER 2.10 (src/context/AuthContext.jsx) ] */
/*
================================================================================
CHANGE MANIFEST
VER 2.10 — 2026-10-01 — RMI-2026-643: drop the OAuth-callback console.log
  Removed the "OAuth callback detected" log inside the isOAuthCallback check (console noise);
  the early return is unchanged. Manifest trimmed to the last 5 entries; VER 2.5 archived in
  docs/INLINE_CHANGELOG_ARCHIVE_3.md.
VER 2.9 — 2026-09-10 — RMI-2026-591: log the deactivated-account storage-clear error (ESLint backlog cleanup)
- The "Back to login" button's localStorage.clear() catch block was
  completely empty, flagged by ESLint's no-empty. Added a console.warn with
  the actual error -- matches the pattern already used for this file's other
  catch block (checkClockSkew's sessionStorage.setItem) and everywhere else
  in the codebase this same lapse was fixed this batch.
- Also trimmed this file's inline manifest to the newest 5 entries; VER
  2.4/2.3/2.2 and the condensed 2.1/2.0/1.9 list (10 entries total, well
  past due) archived verbatim in docs/INLINE_CHANGELOG_ARCHIVE_2.md.
================================================================================
VER 2.8 — 2026-09-01 — RMI-2026-542: fix false-positive clock-skew warnings
- BUG: checkClockSkew's driftSec was `now - expires_at` with no offset for
  the token's own lifetime. For ANY healthy, freshly-issued session that's
  approximately `-expires_in` (~-3600s for Supabase's default 1h tokens) --
  always past the 300s threshold regardless of the client's actual clock.
  The original RMI-2026-480 report (a clock running ~2h fast) produced an
  even larger, still-obviously-wrong number, so the false-positive-on-every-
  normal-session behavior went unnoticed until a user reported seeing the
  warning with a correct clock.
- Compounding it: the check ran on every onAuthStateChange event except
  TOKEN_REFRESHED (explicitly skipped -- backwards, since a refresh is
  exactly when a session is guaranteed freshly issued) including
  INITIAL_SESSION and initializeAuth's own getSession() restore, both of
  which return whatever session is cached in storage -- legitimately
  partway through its lifetime on any normal page load, not a clock signal.
- FIX: driftSec now compares the session's actual remaining lifetime
  (expires_at - now) against what a freshly issued token's remaining
  lifetime should be (expires_in) -- that gap is what actually reflects
  client/server clock disagreement. Restricted the check to only run on
  SIGNED_IN and TOKEN_REFRESHED (both guarantee a fresh issuance); removed
  it from initializeAuth's cached-session-restore path entirely.
=========================================
VER 2.7 — 2026-08-17 — RMI-2026-480: detect + surface client clock skew
- Root cause of the whole login saga this traces back to, confirmed via a
  console screenshot: a user's system clock running ~2 hours fast. gotrue-js
  already logs a console warning for this ("Session as retrieved from URL
  expires in -3600s, should have been closer to 3600s") but nothing
  surfaced it to the user -- they just experienced a silent refresh loop
  (TOKEN_REFRESHED firing 30x in one burst) that got 429 rate-limited by
  Supabase, and a failed refresh made the SDK fire SIGNED_OUT with zero
  indication of why.
- New checkClockSkew(session): compares session.expires_at to Date.now()
  right after a session is set (both in initializeAuth's success path and
  onAuthStateChange). A freshly issued session should have close to its
  full lifetime left; if it already looks expired (or absurdly long-lived)
  by more than 5 minutes the moment it's set, the client's clock -- not the
  session -- is the problem. Flags via sessionStorage (survives the OAuth
  redirect's full page reload, unlike component state) so TigerGate.jsx can
  show a clear one-time message. Detection only -- does not change any
  session/auth control flow.
=========================================
VER 2.6 — 2026-08-17 — RMI-2026-479: visibility handler no longer clears a live session
- User (desktop, Google sign-in): dashboard renders briefly, then jumps back
  to login. Root cause: handleVisibility() is registered on mount --
  including the very page load an OAuth redirect lands back on. A
  visibilitychange event firing on that fresh load raced with the SDK's own
  in-flight session-from-hash parsing; if handleVisibility's getSession()
  resolved first and saw nothing yet, it fell through to refreshSession()
  (also failed -- no refresh token persisted yet either) and then explicitly
  setSession(null)/setProfile(null) -- wiping out a session
  onAuthStateChange had just legitimately set. supabaseClient.js's no-op
  `lock` (a deliberate prior fix for an unrelated navigation bug) removed
  the serialization that used to prevent this exact interleaving.
- Removed the destructive clear-on-failure branch. getSession() already
  auto-refreshes internally when autoRefreshToken is on (it is), so the
  manual refreshSession() fallback was largely redundant; a genuinely dead
  session is already handled correctly and race-free by
  onAuthStateChange's own SIGNED_OUT branch. This handler now only ever
  acts on a session it can positively confirm.
=========================================
================================================================================
*/
